import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Star, Calendar, User, CheckCircle2, MessageSquare, ThumbsUp, ShieldCheck } from "lucide-react";
import api from "@/lib/API";
import { showSuccessMessage, showApiError } from "@/lib/toast";
import { LeadExecutionTable } from './lead_ececution_table';
import { ActionView, ActionEdit, ActionDelete } from "@/components/ui/LatestTableActions";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";
import Modal from "@/components/ui/Modal";

export const FinalReviewTab = ({ project, onUpdate }) => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    rating: 5,
    q1_verveSatisfaction: 'Very Satisfied',
    q2_workSatisfaction: 'Very Satisfied',
    q3_recommend: 'Yes, definitely!',
    q4_timeliness: 'Very Satisfied',
    q5_communication: 'Very Satisfied',
    comments: '',
    // Backwards compatibility
    clientRequirement: '',
    workDone: '',
    teamMembersWorked: ''
  });

  // View Modal state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Delete Modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Hover rating state for Google stars
  const [hoverRating, setHoverRating] = useState(0);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('blum_user') || localStorage.getItem('venus_user') || localStorage.getItem('user') || '{}');
    } catch (e) {
      return {};
    }
  }, []);

  const formatRoleLabel = (role) => {
    if (!role) return 'User';
    const r = String(role).toLowerCase().trim();
    if (r === 'admin' || r === 'superadmin') return 'Admin';
    if (r === 'owner') return 'Owner';
    if (r === 'project_manager' || r === 'pm') return 'Project Manager';
    if (r === 'site_executive') return 'Site Executive';
    if (r === 'designer') return 'Designer';
    if (r === 'telecaller') return 'Telecaller';
    if (r === 'installation_team') return 'Installation Team';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const fetchReviews = async () => {
    if (!project?._id) return;
    try {
      setIsLoading(true);
      const res = await api.get(`/lead-executions/${project._id}`);
      const list = res?.data?.data?.finalReviews || project.finalReviews || [];
      setReviews(list);
    } catch (err) {
      console.error("Error fetching final reviews:", err);
      setReviews(project.finalReviews || []);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (project?._id) {
      setReviews(project.finalReviews || []);
      fetchReviews();
    }
  }, [project?._id]);

  const [receivedAmounts, setReceivedAmounts] = useState([]);

  useEffect(() => {
    if (project?._id) {
       api.get('/received-amounts')
          .then(res => {
             const rawAmounts = res?.data?.data || [];
             const projectAmounts = rawAmounts.filter(item =>
                item.relatedProjectId?._id === project._id ||
                item.relatedProjectId === project._id ||
                item.project === project._id
             );
             setReceivedAmounts(projectAmounts);
          })
          .catch(err => console.error("Failed to fetch received amounts:", err));
    }
  }, [project?._id]);

  const isPaymentComplete = useMemo(() => {
    let totalPaid = project?.paymentSummary?.totalPaid || 0;
    const manualTotal = receivedAmounts.reduce((sum, r) => sum + (Number(r.amount) || Number(r.amountReceived) || 0), 0);
    if (manualTotal > totalPaid) totalPaid = manualTotal;

    const projectValue = Number(project?.projectValue || project?.targetCost || 0);
    return projectValue > 0 && totalPaid >= projectValue;
  }, [project, receivedAmounts]);

  const isInstallationComplete = useMemo(() => {
    if (!project) return false;
    if (Number(project.progress) === 100) return true;

    const activityLog = project.activityLog || [];
    if (activityLog.length === 0) return false;

    const latestUpdatesByTrade = activityLog
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .reduce((acc, curr) => {
        const trade = (curr.trade || 'General').toUpperCase();
        if (!acc[trade]) acc[trade] = curr;
        return acc;
      }, {});

    const trades = Object.values(latestUpdatesByTrade);
    if (trades.length === 0) return false;

    return trades.every(t => Number(t.progress) === 100);
  }, [project]);

  const canAddReview = isPaymentComplete && isInstallationComplete;

  const handleOpenAddModal = () => {
    setEditingReview(null);
    setFormData({
      rating: 5,
      q1_verveSatisfaction: 'Very Satisfied',
      q2_workSatisfaction: 'Very Satisfied',
      q3_recommend: 'Yes, definitely!',
      q4_timeliness: 'Very Satisfied',
      q5_communication: 'Very Satisfied',
      comments: '',
      clientRequirement: '',
      workDone: '',
      teamMembersWorked: ''
    });
    setHoverRating(0);
    setIsAddModalOpen(true);
  };

  const handleOpenEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      rating: review.rating || 5,
      q1_verveSatisfaction: review.q1_verveSatisfaction || 'Very Satisfied',
      q2_workSatisfaction: review.q2_workSatisfaction || 'Very Satisfied',
      q3_recommend: review.q3_recommend || 'Yes, definitely!',
      q4_timeliness: review.q4_timeliness || 'Very Satisfied',
      q5_communication: review.q5_communication || 'Very Satisfied',
      comments: review.comments || review.clientRequirement || '',
      clientRequirement: review.clientRequirement || '',
      workDone: review.workDone || '',
      teamMembersWorked: review.teamMembersWorked || ''
    });
    setHoverRating(0);
    setIsAddModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingReview?._id) {
        const res = await api.put(`/lead-executions/${project._id}/final-reviews/${editingReview._id}`, formData);
        showSuccessMessage("Final Review updated successfully");
        if (res?.data?.data?.finalReviews) {
          setReviews(res.data.data.finalReviews);
        } else {
          fetchReviews();
        }
      } else {
        const res = await api.post(`/lead-executions/${project._id}/final-reviews`, formData);
        showSuccessMessage("Final Review submitted successfully");
        if (res?.data?.data?.finalReviews) {
          setReviews(res.data.data.finalReviews);
        } else {
          fetchReviews();
        }
      }
      setIsAddModalOpen(false);
      setEditingReview(null);
      if (onUpdate) onUpdate(true);
    } catch (err) {
      showApiError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setIsViewModalOpen(true);
  };

  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!reviewToDelete?._id) return;
    setIsDeleting(true);
    try {
      const res = await api.delete(`/lead-executions/${project._id}/final-reviews/${reviewToDelete._id}`);
      showSuccessMessage("Review deleted successfully");
      if (res?.data?.data?.finalReviews) {
        setReviews(res.data.data.finalReviews);
      } else {
        fetchReviews();
      }
      if (onUpdate) onUpdate(true);
    } catch (err) {
      showApiError(err);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setReviewToDelete(null);
    }
  };

  const renderStars = (count, sizeClass = "w-4 h-4") => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${star <= count ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
          />
        ))}
      </div>
    );
  };

  const getRatingLabel = (rating) => {
    if (rating === 5) return "5.0 ★ Excellent";
    if (rating === 4) return "4.0 ★ Good";
    if (rating === 3) return "3.0 ★ Average";
    if (rating === 2) return "2.0 ★ Poor";
    return `${rating}.0 ★`;
  };

  const columns = [
    {
      header: "Review Date",
      render: (item) => (
        <span className="text-[#65768e] text-[11px] font-semibold whitespace-nowrap">
          {new Date(item.reviewDate || item.createdAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      )
    },
    {
      header: "Reviewed By",
      render: (item) => {
        const name = item.reviewedByName || item.addedByName || '—';
        const role = item.reviewedByRole || item.addedByRole || formatRoleLabel(currentUser.role);
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-slate-800 text-[12px] font-bold">{name}</span>
            <span className="inline-block w-fit px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 rounded">
              {role}
            </span>
          </div>
        );
      }
    },
    {
      header: "Rating",
      render: (item) => (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          {renderStars(item.rating || 5, "w-3.5 h-3.5")}
          <span className="px-1.5 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
            {item.rating || 5}.0
          </span>
        </div>
      )
    },
    {
      header: "Review Comments",
      render: (item) => {
        const commentText = item.comments || item.clientRequirement || '—';
        return (
          <div className="text-slate-600 text-[12px] max-w-[280px] truncate" title={commentText}>
            {commentText}
          </div>
        );
      }
    },
    {
      header: "Actions",
      render: (item) => (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <ActionView onClick={() => handleView(item)} title="View Full Review" />
          <ActionEdit onClick={() => handleOpenEditModal(item)} title="Edit Review" />
        </div>
      )
    }
  ];

  const satisfactionOptions = [
    "Very Satisfied",
    "Satisfied",
    "Neutral",
    "Dissatisfied",
    "Very Dissatisfied"
  ];

  const recommendOptions = [
    "Yes, definitely!",
    "Most likely",
    "Not sure",
    "No"
  ];

  return (
    <div className="flex flex-col animate-in fade-in duration-500">
      <div className="flex flex-col w-full">
        <div className="p-1 mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-600 text-[12px]">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span className="font-semibold">Verve Kitchens Customer & Project Quality Review</span>
          </div>
            <button
              onClick={handleOpenAddModal}
              disabled={!canAddReview}
              title={!canAddReview ? "Review can only be added when installation is 100% complete and 100% payment is received." : "Add Final Review"}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-[11px] font-semibold shadow-sm transition-colors border ${
                canAddReview 
                  ? 'cursor-pointer border-[#2b448f] bg-white text-[#2b448f] hover:bg-slate-50' 
                  : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
              }`}
            >
              <Plus className="w-3.5 h-3.5" />
              Add Final Review
            </button>
        </div>

        <LeadExecutionTable
          columns={columns}
          data={reviews}
          isLoading={isLoading}
        />
      </div>

      {/* Add / Edit Review Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => { setIsAddModalOpen(false); setEditingReview(null); }}
        title={editingReview ? "Edit Final Review" : "Add Final Review (Google Rating & Satisfaction)"}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-1 max-h-[80vh] overflow-y-auto pr-1">
          {/* Capture Date & Role Banner */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2 text-[12px] text-slate-600">
            <div className="flex items-center gap-1.5 font-medium">
              <User className="w-4 h-4 text-[#2b448f]" />
              <span>Reviewing as: <strong className="text-slate-800">{currentUser.name || 'User'}</strong></span>
              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-800">
                {formatRoleLabel(currentUser.role)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 font-medium text-slate-500">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Date: <strong>{new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
            </div>
          </div>

          {/* Google Style Overall Rating */}
          <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-4 flex flex-col items-center justify-center gap-2 text-center shadow-sm">
            <label className="text-[13px] font-bold text-slate-800 tracking-wide uppercase">
              Overall Rating
            </label>
            <div className="flex items-center gap-1 my-1">
              {[1, 2, 3, 4, 5].map((star) => {
                const isActive = star <= (hoverRating || formData.rating);
                return (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-125 focus:outline-none cursor-pointer"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        isActive ? "text-amber-400 fill-amber-400" : "text-slate-200"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
            <span className="text-[12px] font-bold text-amber-800 bg-amber-100/80 px-3 py-1 rounded-full border border-amber-300/50">
              {getRatingLabel(hoverRating || formData.rating)}
            </span>
          </div>

          <div className="h-px bg-slate-100 my-1" />

          {/* 5 Questions Section */}
          <div className="flex flex-col gap-4">
            <h4 className="text-[12px] font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
              <ThumbsUp className="w-4 h-4 text-[#2b448f]" />
              Customer Satisfaction Questionnaire (5 Questions)
            </h4>

            {/* Q1 */}
            <div className="bg-white border border-slate-200 rounded-lg p-3.5 flex flex-col gap-2">
              <label className="text-[13px] font-bold text-slate-800">
                1. How satisfied are you with our Verve Kitchens overall? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {satisfactionOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelectChange('q1_verveSatisfaction', option)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer ${
                      formData.q1_verveSatisfaction === option
                        ? 'bg-[#2b448f] text-white shadow-sm ring-2 ring-[#2b448f]/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Q2 */}
            <div className="bg-white border border-slate-200 rounded-lg p-3.5 flex flex-col gap-2">
              <label className="text-[13px] font-bold text-slate-800">
                2. How satisfied are you with our continuous work and installation quality? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {satisfactionOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelectChange('q2_workSatisfaction', option)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer ${
                      formData.q2_workSatisfaction === option
                        ? 'bg-[#2b448f] text-white shadow-sm ring-2 ring-[#2b448f]/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Q3 */}
            <div className="bg-white border border-slate-200 rounded-lg p-3.5 flex flex-col gap-2">
              <label className="text-[13px] font-bold text-slate-800">
                3. Would you recommend Verve Kitchens to friends and family? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {recommendOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelectChange('q3_recommend', option)}
                    className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer ${
                      formData.q3_recommend === option
                        ? 'bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-600/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Q4 */}
            <div className="bg-white border border-slate-200 rounded-lg p-3.5 flex flex-col gap-2">
              <label className="text-[13px] font-bold text-slate-800">
                4. How satisfied are you with our project delivery timeline and schedule? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {satisfactionOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelectChange('q4_timeliness', option)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer ${
                      formData.q4_timeliness === option
                        ? 'bg-[#2b448f] text-white shadow-sm ring-2 ring-[#2b448f]/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Q5 */}
            <div className="bg-white border border-slate-200 rounded-lg p-3.5 flex flex-col gap-2">
              <label className="text-[13px] font-bold text-slate-800">
                5. How satisfied are you with our team communication and responsiveness? <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {satisfactionOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleSelectChange('q5_communication', option)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all cursor-pointer ${
                      formData.q5_communication === option
                        ? 'bg-[#2b448f] text-white shadow-sm ring-2 ring-[#2b448f]/20'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="h-px bg-slate-100 my-1" />

          {/* Review Comments */}
          <div>
            <label className="block text-[13px] font-bold text-slate-800 mb-1.5 flex items-center gap-1.5">
              <MessageSquare className="w-4 h-4 text-[#2b448f]" />
              Review Comments & Feedback <span className="text-red-500">*</span>
            </label>
            <textarea
              name="comments"
              value={formData.comments}
              onChange={handleInputChange}
              rows={3}
              placeholder="Write detailed review comments, customer feedback, or overall experience summary..."
              className="w-full text-[13px] p-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#2b448f] focus:ring-1 focus:ring-[#2b448f] text-slate-800"
              required
            />
          </div>

          <div className="flex justify-end items-center gap-3 pt-3 border-t border-slate-100 mt-2">
            <button
              type="button"
              onClick={() => { setIsAddModalOpen(false); setEditingReview(null); }}
              className="px-4 py-2 text-[12px] font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 text-[12px] font-semibold text-white bg-[#2b448f] rounded-lg hover:bg-[#1f326b] transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
            >
              {isSubmitting ? "Saving..." : editingReview ? "Update Review" : "Submit Review"}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Full Data Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Final Review Details"
      >
        {selectedReview && (
          <div className="flex flex-col gap-4 p-1 max-h-[80vh] overflow-y-auto pr-1">
            {/* Header info */}
            <div className="flex items-center justify-between text-[12px] text-slate-600 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2 font-medium">
                <User className="w-4 h-4 text-[#2b448f]" />
                <span className="font-bold text-slate-800">{selectedReview.reviewedByName || selectedReview.addedByName || '—'}</span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                  {selectedReview.reviewedByRole || selectedReview.addedByRole || formatRoleLabel(currentUser.role)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 font-medium text-slate-500">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span>Reviewed on {new Date(selectedReview.reviewDate || selectedReview.createdAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </div>
            </div>

            {/* Overall Rating Box */}
            <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-3.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                <span className="text-[13px] font-bold text-slate-800">Overall Google Rating:</span>
              </div>
              <div className="flex items-center gap-2">
                {renderStars(selectedReview.rating || 5, "w-4 h-4")}
                <span className="px-2.5 py-0.5 rounded-full font-bold bg-amber-100 text-amber-900 text-[12px] border border-amber-300/60">
                  {selectedReview.rating || 5}.0 ★
                </span>
              </div>
            </div>

            {/* Questions Summary */}
            <div className="flex flex-col gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                Customer Satisfaction Questionnaire
              </h4>

              <div className="flex flex-col gap-2.5 text-[12px]">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
                  <span className="text-slate-700 font-medium">1. Satisfaction with Verve Kitchens overall:</span>
                  <span className="font-bold text-[#2b448f] bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                    {selectedReview.q1_verveSatisfaction || 'Very Satisfied'}
                  </span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
                  <span className="text-slate-700 font-medium">2. Continuous work & installation quality:</span>
                  <span className="font-bold text-[#2b448f] bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                    {selectedReview.q2_workSatisfaction || 'Very Satisfied'}
                  </span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
                  <span className="text-slate-700 font-medium">3. Recommend to friends and family:</span>
                  <span className="font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-100">
                    {selectedReview.q3_recommend || 'Yes, definitely!'}
                  </span>
                </div>

                <div className="flex justify-between items-center pb-2 border-b border-slate-200/60">
                  <span className="text-slate-700 font-medium">4. Project delivery timeline & schedule:</span>
                  <span className="font-bold text-[#2b448f] bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                    {selectedReview.q4_timeliness || 'Very Satisfied'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-slate-700 font-medium">5. Team communication & responsiveness:</span>
                  <span className="font-bold text-[#2b448f] bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                    {selectedReview.q5_communication || 'Very Satisfied'}
                  </span>
                </div>
              </div>
            </div>

            {/* Comments Box */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5 text-[#2b448f]" />
                Review Comments & Feedback
              </h4>
              <p className="text-[13px] text-slate-800 whitespace-pre-wrap font-medium leading-relaxed">
                {selectedReview.comments || selectedReview.clientRequirement || '—'}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 mt-2">
              <button
                type="button"
                onClick={() => setIsViewModalOpen(false)}
                className="px-5 py-2 text-[12px] font-semibold text-white bg-[#2b448f] rounded-lg hover:bg-[#1f326b] transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Final Review"
        message="Are you sure you want to delete this final review entry? This action cannot be undone."
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
      />
    </div>
  );
};

export default FinalReviewTab;
